import * as Phaser from 'phaser';
import { UITheme } from './UITheme';

export interface UIFocusTarget {
  setFocused(focused: boolean): void;
  activate(): void;
  adjust?(direction: number): void;
}

const focusGroups = new WeakMap<Phaser.Scene, object[]>();

/** Верхнее открытое окно получает клавиатуру; закрытие восстанавливает прежний фокус. */
export function bindUIFocus(scene: Phaser.Scene, targets: UIFocusTarget[]): () => void {
  const stack = focusGroups.get(scene) ?? [];
  focusGroups.set(scene, stack);
  const group = {};
  stack.push(group);
  let index = -1;
  let disposed = false;
  const keyboard = scene.input.keyboard;
  const onKey = (event: KeyboardEvent) => {
    if (stack[stack.length - 1] !== group || !targets.length) return;
    const tag = (event.target as HTMLElement | null)?.tagName;
    if (tag && !['BODY', 'CANVAS', 'HTML'].includes(tag)) return;
    const key = event.key;
    if (!['Tab', 'ArrowLeft', 'ArrowRight', 'ArrowUp', 'ArrowDown', 'Enter', ' '].includes(key)) return;
    event.preventDefault();
    event.stopPropagation();
    if ((key === 'Enter' || key === ' ') && event.repeat) return;
    if ((key === 'ArrowLeft' || key === 'ArrowRight') && index >= 0 && targets[index]!.adjust) {
      targets[index]!.adjust!(key === 'ArrowLeft' ? -1 : 1);
    } else if (key === 'Enter' || key === ' ') {
      if (index < 0) { index = 0; targets[index]!.setFocused(true); }
      targets[index]!.activate();
    } else {
      if (index >= 0) targets[index]!.setFocused(false);
      const backwards = (key === 'Tab' && event.shiftKey) || key === 'ArrowLeft' || key === 'ArrowUp';
      index = index < 0 ? (backwards ? targets.length - 1 : 0) : (index + (backwards ? -1 : 1) + targets.length) % targets.length;
      targets[index]!.setFocused(true);
    }
  };
  keyboard?.on('keydown', onKey);
  const onShutdown = () => dispose(false);
  const dispose = (clearFocus = true) => {
    if (disposed) return;
    disposed = true;
    if (clearFocus && index >= 0) targets[index]!.setFocused(false);
    stack.splice(stack.indexOf(group), 1);
    keyboard?.off('keydown', onKey);
    scene.events.off('shutdown', onShutdown);
  };
  scene.events.once('shutdown', onShutdown);
  return dispose;
}

export class UIButton extends Phaser.GameObjects.Container implements UIFocusTarget {
  readonly background: Phaser.GameObjects.Rectangle;
  readonly label: Phaser.GameObjects.Text;
  private enabled = true;
  private focused = false;
  private hovered = false;
  private pressed = false;

  constructor(scene: Phaser.Scene, x: number, y: number, text: string, private readonly action: () => void, width = 220, height = 52, private readonly secondary = false) {
    super(scene, x, y);
    this.setScrollFactor(0);
    this.background = scene.add.rectangle(0, 0, width, height, secondary ? UITheme.panel : UITheme.accent);
    this.label = scene.add.text(0, 0, text, { fontFamily: UITheme.body, fontSize: '20px', color: secondary ? UITheme.text : '#101f1c', fontStyle: 'bold', align: 'center', wordWrap: { width: width - 20 } }).setOrigin(0.5);
    this.add([this.background, this.label]);
    this.setInteractive(new Phaser.Geom.Rectangle(-width / 2, -height / 2, width, height), Phaser.Geom.Rectangle.Contains);
    this.on('pointerover', () => { this.hovered = true; this.paint(); });
    this.on('pointerout', () => { this.hovered = false; this.pressed = false; this.paint(); });
    this.on('pointerdown', (_p: unknown, _x: number, _y: number, event?: Phaser.Types.Input.EventData) => {
      event?.stopPropagation(); this.pressed = this.enabled; this.paint();
    });
    this.on('pointerup', (_p: unknown, _x: number, _y: number, event?: Phaser.Types.Input.EventData) => {
      event?.stopPropagation(); const pressed = this.pressed; this.pressed = false; this.paint(); if (pressed) this.activate();
    });
    scene.add.existing(this);
  }
  setText(text: string): this { this.label.setText(text); return this; }
  setEnabled(enabled: boolean): this { this.enabled = enabled; this.pressed = false; this.paint(); return this; }
  setFocused(focused: boolean): void { this.focused = focused; this.paint(); }
  activate(): void { if (this.enabled && this.visible) this.action(); }
  private paint(): void {
    this.background.setFillStyle(!this.enabled ? 0x526057 : this.pressed ? (this.secondary ? UITheme.hover : 0x8db95b) : this.hovered ? (this.secondary ? UITheme.hover : 0xd0f49c) : this.secondary ? UITheme.panel : UITheme.accent);
    this.background.setStrokeStyle(this.focused ? 3 : 1, this.focused ? UITheme.warning : UITheme.panel);
    this.label.setColor(this.enabled ? (this.secondary ? UITheme.text : '#101f1c') : UITheme.muted);
  }
}
